import { z } from 'zod';
import { ConversationStatusValueSchema } from '@wit-support/contracts';
import { ConversationId } from './ids.js';
import { TagRefDTO } from './tags.js';
/**
 * Admin-facing schemas for «слить все диалоги контакта в этот» — the N-way
 * merge (`ConversationMergePort.mergeMany` / `.preview`) behind
 * `GET /api/conversations/:id/merge-preview` +
 * `POST /api/conversations/:id/merge-all`.
 *
 * IMPORT DISCIPLINE: this file is a LEAF next to `ids.ts`/`tags.ts`. It pulls
 * the conversation-status enum straight from `@wit-support/contracts` rather
 * than re-importing the re-export in `./conversations.js`, and NOTHING in this
 * package may import `merge.ts` from `conversations.ts` — the same mutual-import
 * hazard `ids.ts` exists to prevent (see its header, and the regression test in
 * `comments.test.ts`).
 */
/** Re-exported from `@wit-support/contracts` — canonical source of truth. */
const ConversationStatus = ConversationStatusValueSchema;
/**
 * One candidate source row as the merge dialog renders it: enough to let the
 * operator recognise the thread (number + subject + status) and to judge what
 * they are about to lose (message count, tags, last activity).
 *
 * `number` is nullable for the same reason `ConversationDTO.number` is — a
 * rollout window in which a row predates `conversation.number`.
 */
export const MergePreviewSourceDTO = z.object({
    id: ConversationId,
    number: z.number().int().nullable(),
    subject: z.string().nullable(),
    status: ConversationStatus,
    messageCount: z.number().int().nonnegative(),
    tags: z.array(TagRefDTO),
    lastActivityAt: z.string().datetime(),
    /**
     * The sender is blacklisted (`blocked_at` set). One such row closes the
     * whole merged thread, so the dialog marks it. `default(false)` for the
     * rollout window in which a new bundle reads a field the old admin-api does
     * not yet send — same guard as `ConversationDTO.number`.
     */
    blocked: z.boolean().default(false),
});
/**
 * «Что произойдёт» — the very same `computeMergePlan` output the engine will
 * act on, so the dialog can never promise an outcome the merge does not
 * deliver. The `cancelled*` ids are collapsed to COUNTS on the wire: the dialog
 * shows «Отложенных писем будет отменено: N», and shipping the raw ids would
 * hand the client a list it has no route to act on.
 */
export const MergePreviewPlanDTO = z.object({
    resultStatus: ConversationStatus,
    cancelledScheduledEmailCount: z.number().int().nonnegative(),
    cancelledPipelineRunCount: z.number().int().nonnegative(),
    resultTags: z.array(TagRefDTO),
});
export const MergePreviewResponse = z.object({
    targetId: ConversationId,
    sources: z.array(MergePreviewSourceDTO),
    plan: MergePreviewPlanDTO,
});
/**
 * `GET /:id/merge-preview?source=<uuid>&source=<uuid>` — the optional subset
 * the PLAN is computed for. `sources` in the response stays the full live set
 * regardless; only `plan` follows the subset. Absent ⇒ the plan covers every
 * live sibling. Capped like `MergeAllRequest.sourceIds`.
 */
export const MergePreviewQuery = z.object({
    source: z.array(ConversationId).max(100).optional(),
});
/**
 * `sourceIds` is the set the operator CONFIRMED, echoed back so the server can
 * detect a stale preview (a thread arrived, or somebody else merged first) and
 * answer `409 preview_stale` instead of silently merging a different set than
 * the one on screen. Compared as a SET — order is a rendering detail.
 *
 * Capped at 100 to match `MergeManyInputSchema`; `min(1)` because a merge of
 * nothing is a mistake, not a no-op worth a 200.
 */
export const MergeAllRequest = z
    .object({
    sourceIds: z.array(ConversationId).min(1).max(100),
    note: z.string().trim().max(1000).optional(),
})
    // A repeated id is a malformed body, not a stale preview: reject it here as
    // `400 invalid_body` so the route's set-vs-list guard never has to translate
    // `[a, a]` into a misleading `409 preview_stale` (and the engine's own
    // uniqueness refinement never sees it as a 500).
    .refine((v) => new Set(v.sourceIds).size === v.sourceIds.length, {
    message: 'sourceIds must be unique',
    path: ['sourceIds'],
});
/**
 * Mirrors `MergeManyResult`. The cancelled ids ARE shipped here (unlike the
 * preview's counts): this is the record of what actually happened, the same
 * payload the audit-log diff carries.
 */
export const MergeAllResponse = z.object({
    targetId: ConversationId,
    mergedConversationIds: z.array(ConversationId),
    mergedMessageCount: z.number().int().nonnegative(),
    cancelledScheduledEmailIds: z.array(z.string()),
    cancelledPipelineRunIds: z.array(z.string()),
    statusFrom: ConversationStatus,
    statusTo: ConversationStatus,
});
//# sourceMappingURL=merge.js.map